import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';

/**
 * Angular service for Real Estate Property module.
 *
 * Uses entityId throughout — no sku, no propertyId, no product_id.
 * Backend base: serverAPI9 (ecom-svc-crm) → /api/property
 */
@Injectable({
  providedIn: 'root'
})
export class RealEstatePropertyService {

  constructor(private http: HttpClient) { }

  // Base URLs
  private propertyBase = environment.serverAPI9 + '/api/property';
  private eavBase = environment.serverAPI + '/api/eavattribute';

  // ─── SOLR (LIST / SEARCH) ──────────────────────────────────────────────────

  /**
   * Get paginated property list from Solr index.
   */
  getProperties(page: number, size: number, sort: string): Observable<any> {
    return this.http.get(`${this.propertyBase}/solr?page=${page}&size=${size}&sort=${sort}`);
  }

  /**
   * Advanced filter search against Solr property_idx.
   */
  searchProperties(query: string, page: number, size: number, sort: string, additionalQuery?: any): Observable<any> {
    const requestBody = {
      "searchQuery": query != null ? query.split("'").join('') : null,
      "groupBy": null,
      "additionalQuery": additionalQuery
    };

    const queryParams = `?page=${page}&size=${size}&sort=${sort}`;

    return this.http.post(`${this.propertyBase}/solr/filter${queryParams}`, requestBody);
  }

  searchPropertyAdvanceFilter(query: string, page: number, size: number, sort: string, additionalQuery?: any): Observable<any> {
    return this.searchProperties(query, page, size, sort, additionalQuery);
  }

  // ─── DB (SINGLE PROPERTY WITH EAV) ────────────────────────────────────────

  /**
   * Get a single property with full EAV from the database.
   * @param entityId the property entity_id
   */
  getPropertyById(entityId: number): Observable<any> {
    return this.http.get(`${this.propertyBase}/db/${entityId}`);
  }

  /**
   * Get all properties (DB, paginated).
   */
  getAllProperties(page: number, size: number, sort: string): Observable<any> {
    return this.http.get(`${this.propertyBase}?page=${page}&size=${size}&sort=${sort}`);
  }

  // ─── CREATE / UPDATE ───────────────────────────────────────────────────────

  /**
   * Save a new property. Body includes static fields + EAV arrays.
   */
  saveProperty(property: any): Observable<any> {
    return this.http.post(this.propertyBase, property);
  }

  /**
   * Update an existing property by entityId.
   */
  updateProperty(entityId: number, property: any): Observable<any> {
    return this.http.put(`${this.propertyBase}/${entityId}`, property);
  }

  // ─── DELETE ────────────────────────────────────────────────────────────────

  /**
   * Delete a single property by entityId.
   */
  deleteProperty(entityId: number): Observable<any> {
    return this.http.delete(`${this.propertyBase}/${entityId}`);
  }

  /**
   * Delete multiple selected properties.
   */
  deleteSelectedProperties(entityIds: number[]): Observable<any> {
    return this.http.post(`${this.propertyBase}/delete/selected`, entityIds);
  }

  // ─── DROPDOWN ─────────────────────────────────────────────────────────────

  /**
   * Get active properties for dropdown selection.
   */
  getActivePropertyDropdown(): Observable<any> {
    return this.http.get(`${this.propertyBase}/dropdown/active`);
  }

  // ─── VALIDATION ───────────────────────────────────────────────────────────

  /**
   * Check if a property name already exists for the tenant.
   */
  checkPropertyCodexists(propertyName, propertyCode): Observable<any> {
    return this.http.post(`${this.propertyBase}/exists`, { name: propertyName, sku: propertyCode });
  }

  // ─── IMPORT / EXPORT ──────────────────────────────────────────────────────

  /**
   * Download Excel report for a property (attribute set).
   */
  downloadPropertyReport(attributeSetId: number): Observable<any> {
    return this.http.get(`${this.propertyBase}/export/${attributeSetId}`, {
      responseType: 'arraybuffer',
      observe: 'response'
    });
  }

  /**
   * Import properties from Excel file.
   */
  importProperties(data: FormData, attributeSetId: number): Observable<any> {
    return this.http.post(`${this.propertyBase}/import/${attributeSetId}`, data);
  }

  /**
   * Upload property media zip.
   */
  uploadPropertyMediaZip(data: FormData, entityId: number): Observable<any> {
    return this.http.post(`${this.propertyBase}/media/zip/${entityId}`, data);
  }

  /**
   * Delete property media by mediaId.
   */
  deletePropertyMedia(mediaId: number): Observable<any> {
    return this.http.delete(`${this.propertyBase}/media/${mediaId}`);
  }

  // ─── EAV ATTRIBUTES ───────────────────────────────────────────────────────

  /**
   * Get EAV attribute definitions for Property (entity type 15).
   */
  getEavAttributes(attributeSetId?: number): Observable<any> {
    let params = new HttpParams();
    if (attributeSetId) {
      params = params.set('attributeSetId', attributeSetId.toString());
    }
    return this.http.get(`${this.eavBase}/property`, { params });
  }

  // ─── SOLR INDEX ───────────────────────────────────────────────────────────

  /**
   * Trigger full re-index of all properties for the current tenant.
   */
  reindexAllProperties(): Observable<any> {
    return this.http.post(`${this.propertyBase}/reindex`, {});
  }

  /**
   * Trigger re-index for a single property.
   */
  reindexProperty(entityId: number): Observable<any> {
    return this.http.post(`${this.propertyBase}/reindex/${entityId}`, {});
  }
}
